import React, { useCallback, useState } from 'react'
import DashboardLayout from '../../../../layouts/Dashboard'
import { Col, Row, Container, Button } from 'react-bootstrap'
import { useParams } from 'react-router-dom'

import DriverInfo from '../../../../components/Driver/DriverInfo'
import DriverAlerts from '../../../../components/Driver/DriverAlerts'
import DriverPerformance from '../../../../components/Driver/DriverPerformance'
import VehicleHistorical from '../../../../components/Vehicle/VehicleHistorical'
import api from '../../../../services/api'
import { toast } from 'react-toastify'
import ModalTable from '../../../../components/ModalTable'
import { formatMoney } from '../../../../utils/format'
import { formatDateTSX } from '../../../../utils/tsxFormat'
import { PencilSimpleLine, X } from 'phosphor-react'
import DatePicker from 'react-date-picker'
import { useLoading } from '../../../../hooks/LoadingContext'

interface ITicket {
  date: Date
  type: string
  cost: number
  points: number
  description: string
}

interface IRequest {
  invoice: string
  request_distance_km: number
  request_time_min: number
  driver_note: number
  place: typeof Button
}

const MotoristasInfo: React.FC = () => {
  let { id } = useParams()
  const [driverVehicleId, setDriverVehicleId] = useState(0)
  const [showTicketsModal, setShowTicketsModal] = useState(false)
  const [showRequestsModal, setShowRequestsModal] = useState(false)
  const [tickets, setTickets] = useState<ITicket[]>()
  const [requests, setRequests] = useState<IRequest[]>()

  const [alertMonth, setAlertMonth] = useState<any>({
    pickerDate: '',
    date: '',
  })
  const [performanceMonth, setPerformanceMonth] = useState<any>({
    pickerDate: '',
    date: '',
  })

  const [isPerformancePickerOpen, setIsPerformancePickerOpen] = useState(false)
  const [isAlertPickerOpen, setIsAlertPickerOpen] = useState(false)

  const { setIsLoading } = useLoading()

  const ticketHeader = ['Data', 'Tipo', 'Custo', 'Pontos', 'Descrição']
  const requestHeader = [
    'Nota fiscal',
    'Distância da entrega (km)',
    'Tempo de entrega (min)',
    'Nota de entrega do motorista',
    'Local de entrega',
  ]

  const handleAlertIconClick = () => {
    setIsAlertPickerOpen(!isAlertPickerOpen)
  }

  const handlePerformanceIconClick = () => {
    setIsPerformancePickerOpen(!isPerformancePickerOpen)
  }

  const loadTicketsData = useCallback(async () => {
    let apiUrl = `/company/traffic-tickets?driver_id=${id}&limit=10`
    const offset = tickets ? tickets.length : 0
    if (offset) apiUrl += '&offset=' + offset
    try {
      setIsLoading(true)
      const { data } = await api.get(apiUrl)
      if (!data.length && offset !== 0)
        toast.info('Não há mais multas resgistradas para o motorista.')
      else {
        const newData = data.map((ticket: ITicket) => {
          return {
            date: formatDateTSX(ticket.date, 'dd/MM/yyyy'),
            type: ticket.type,
            cost: formatMoney(ticket.cost),
            points: ticket.points,
            description: ticket.description,
          }
        })
        setTickets(tickets ? tickets.concat(newData) : newData)
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
  }, [id, tickets, setIsLoading])

  const handleShowTickets = () => {
    setShowTicketsModal(true)

    if (!tickets || tickets.length === 0) {
      loadTicketsData()
    }
  }

  const loadRequestsData = useCallback(async () => {
    let apiUrl = `/company/requests?status=delivered&driver_id=${id}&limit=10`
    const offset = requests ? requests.length : 0
    if (offset) apiUrl += '&offset=' + offset
    try {
      setIsLoading(true)
      const { data } = await api.get(apiUrl)

      if (!data.length && offset !== 0)
        toast.info('Não há mais entregas registradas para o motorista.')
      else {
        const newData = data.map((request: any) => {
          return {
            invoice: request.invoice.substring(0, 10),
            request_distance_km: (request.request_delivery_distance_meters / 1000).toFixed(2),
            request_time_min: (request.request_delivery_time_seconds / 60).toFixed(2),
            driver_note: request.request_delivery_driver_note,
            place: (
              <Button
                href={`https://www.google.com/maps/search/?api=1&query=${request.lat},${request.lng}`}
                target="_blank"
                style={{
                  fontSize: 14,
                  backgroundColor: '#053C5E',
                  border: 'none',
                  borderRadius: '10px',
                  textAlign: 'center',
                }}
              >
                Mostrar local
              </Button>
            ),
          }
        })
        setRequests(requests ? requests.concat(newData) : newData)
      }
    } catch (err) {
      toast.error('Falha ao buscar os dados! Atualize a página.')
    }
    setIsLoading(false)
  }, [id, requests, setIsLoading])

  const handleShowRequests = () => {
    setShowRequestsModal(true)

    if (!requests || requests.length === 0) {
      loadRequestsData()
    }
  }

  return (
    <DashboardLayout backgroundColor="#F8F8F8" pageName="Informações do motorista">
      <Container>
        <Row>
          <Col md={6}>
            <h3 className="mt-4 mb-3">Informações do motorista</h3>
            <span className="ml-5">
              <DriverInfo driverId={id} setVehicleId={setDriverVehicleId}></DriverInfo>
            </span>
          </Col>

          <Col md={6}>
            <div className="d-flex flex-row align-items-center mt-4">
              <h3 style={{ marginLeft: '-2%', marginRight: 10 }} className="mb-0">
                Alertas
              </h3>
              <span className="mt-1 d-flex align-items-center">
                <span>
                  {alertMonth.date
                    ? new Date(alertMonth.date).toLocaleString('pt-BR', {
                        month: 'short',
                        year: 'numeric',
                        timeZone: 'UTC',
                      })
                    : 'Todo o período'}
                </span>
                <PencilSimpleLine
                  size={20}
                  color="#053C5E"
                  style={{ marginLeft: 5, cursor: 'pointer' }}
                  onClick={handleAlertIconClick}
                />
                {alertMonth.date && (
                  <X
                    size={20}
                    weight="bold"
                    color="#DB222A"
                    style={{ marginLeft: 5, cursor: 'pointer' }}
                    onClick={() => setAlertMonth({ pickerDate: '', date: '' })}
                  />
                )}

                <DatePicker
                  value={alertMonth.pickerDate}
                  isOpen={isAlertPickerOpen}
                  maxDate={new Date()}
                  onChange={(value: any) =>
                    setAlertMonth({
                      pickerDate: value,
                      date: value.toISOString().substring(0, 7),
                    })
                  }
                  onBlur={() => setIsAlertPickerOpen(false)}
                  onCalendarClose={() => setIsAlertPickerOpen(false)}
                  calendarProps={{
                    defaultView: 'decade',
                    maxDetail: 'year',
                    minDetail: 'decade',
                  }}
                />
              </span>
            </div>
            <DriverAlerts
              driverId={id}
              handleShowTickets={handleShowTickets}
              date={alertMonth.date}
            ></DriverAlerts>
          </Col>
        </Row>
        <Row>
          <Col md={6}>
            <h3 className="mt-4 mt-md-3 mb-3">Histórico do veículo</h3>
            <span className="ml-5">
              <VehicleHistorical vehicleId={driverVehicleId} role="company"></VehicleHistorical>
            </span>
          </Col>
          <Col md={6}>
            <div className="d-flex flex-row align-items-center mt-4">
              <h3 style={{ marginLeft: '-2%', marginRight: 10 }} className="mb-0">
                Desempenho
              </h3>
              <span className="mt-1 d-flex align-items-center">
                <span>
                  {performanceMonth.date
                    ? new Date(performanceMonth.date).toLocaleString('pt-BR', {
                        month: 'short',
                        year: 'numeric',
                        timeZone: 'UTC',
                      })
                    : 'Todo o período'}
                </span>
                <PencilSimpleLine
                  size={20}
                  color="#053C5E"
                  style={{ marginLeft: 5, cursor: 'pointer' }}
                  onClick={handlePerformanceIconClick}
                />

                {performanceMonth.date && (
                  <X
                    size={20}
                    weight="bold"
                    color="#DB222A"
                    style={{ marginLeft: 5, cursor: 'pointer' }}
                    onClick={() => setPerformanceMonth({ pickerDate: '', date: '' })}
                  />
                )}

                <DatePicker
                  value={performanceMonth.pickerDate}
                  isOpen={isPerformancePickerOpen}
                  maxDate={new Date()}
                  onChange={(value: any) =>
                    setPerformanceMonth({
                      pickerDate: value,
                      date: value.toISOString().substring(0, 7),
                    })
                  }
                  onBlur={() => setIsPerformancePickerOpen(false)}
                  onCalendarClose={() => setIsPerformancePickerOpen(false)}
                  calendarProps={{
                    defaultView: 'decade',
                    maxDetail: 'year',
                    minDetail: 'decade',
                  }}
                />
              </span>
            </div>
            <DriverPerformance
              driverId={id}
              handleShowRequests={handleShowRequests}
              date={performanceMonth.date}
            ></DriverPerformance>
          </Col>
        </Row>
        <ModalTable
          title={'Infrações de trânsito'}
          headers={ticketHeader}
          data={tickets}
          onHide={() => setShowTicketsModal(false)}
          loadMoreData={loadTicketsData}
          show={showTicketsModal}
          buttonText="Carregar próximas multas"
          missingDataText="Nenhuma infração encontrada!"
        />

        <ModalTable
          title={'Requisições de entrega'}
          headers={requestHeader}
          data={requests}
          onHide={() => setShowRequestsModal(false)}
          loadMoreData={loadRequestsData}
          show={showRequestsModal}
          buttonText="Carregar próximas entregas"
          missingDataText="Nenhuma entrega encontrada!"
        />
      </Container>
    </DashboardLayout>
  )
}

export default MotoristasInfo
